import { Lang } from '../i18n/I18nProvider';

/**
 * Ngày trong ứng dụng này luôn là ngày trên lịch của chính người dùng, không phải một mốc thời gian.
 * Vì vậy nó được biểu diễn bằng chuỗi "YYYY-MM-DD" và không bao giờ đi qua `toISOString()` — hàm đó
 * quy về UTC, và với người ở UTC+7 thì nửa đêm hôm nay sẽ thành 17 giờ hôm qua.
 */
export type DateKey = string;

const WEEKDAYS: Record<Lang, string[]> = {
  vi: ['Chủ Nhật', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy'],
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
};

const MONTHS_SHORT_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function pad(value: number): string {
  return value < 10 ? '0' + value : String(value);
}

/** Lấy khóa ngày từ một Date, đọc theo múi giờ của máy. */
export function toDateKey(value: Date): DateKey {
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}

/** Dựng lại Date ở đúng nửa đêm địa phương của ngày đó. */
export function parseDateKey(key: DateKey): Date {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function todayKey(): DateKey {
  return toDateKey(new Date());
}

export function addDays(key: DateKey, days: number): DateKey {
  const date = parseDateKey(key);
  date.setDate(date.getDate() + days);
  return toDateKey(date);
}

export function isValidDateKey(key: string | undefined): key is DateKey {
  if (!key || !/^\d{4}-\d{2}-\d{2}$/.test(key)) {
    return false;
  }

  const date = parseDateKey(key);
  return !Number.isNaN(date.getTime()) && toDateKey(date) === key;
}

/** "Chủ Nhật, 30/08/2026" · "Sunday, 30 Aug 2026" */
export function formatDayLong(key: DateKey, lang: Lang): string {
  const date = parseDateKey(key);
  const weekday = WEEKDAYS[lang][date.getDay()];

  if (lang === 'en') {
    return `${weekday}, ${date.getDate()} ${MONTHS_SHORT_EN[date.getMonth()]} ${date.getFullYear()}`;
  }

  return `${weekday}, ${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

/** "29/08" — dạng ngắn dùng chung cho cả hai ngôn ngữ. */
export function formatDayShort(key: DateKey): string {
  const date = parseDateKey(key);
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}`;
}

export type RelativeDay = 'day.today' | 'day.yesterday' | 'day.tomorrow';

/**
 * Trả về KHÓA của nhãn tương đối chứ không phải chữ đã dịch — việc dịch là của tầng giao diện.
 * Ngoài ba ngày quanh hôm nay thì không có nhãn nào tự nhiên cả.
 */
export function relativeDayKey(key: DateKey): RelativeDay | null {
  const today = todayKey();

  if (key === today) return 'day.today';
  if (key === addDays(today, -1)) return 'day.yesterday';
  if (key === addDays(today, 1)) return 'day.tomorrow';

  return null;
}

/** "17:00" từ một mốc thời gian ISO trả về từ API. */
export function formatClock(instant: string | undefined): string {
  if (!instant) return '';

  const date = new Date(instant);
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** "17:00 29/08/2026" */
export function formatInstant(instant: string | undefined): string {
  if (!instant) return '';

  const date = new Date(instant);
  return `${formatClock(instant)} ${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

/** Mốc mục tiêu đã trôi qua — dùng để tô cảnh báo khi việc vẫn chưa xong. */
export function isOverdue(instant: string | undefined): boolean {
  return !!instant && new Date(instant).getTime() < Date.now();
}

export function formatMinutes(minutes: number | undefined, lang: Lang): string {
  if (!minutes) return '';

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;

  if (lang === 'en') {
    if (hours === 0) return `${rest}m`;
    if (rest === 0) return `${hours}h`;
    return `${hours}h ${rest}m`;
  }

  if (hours === 0) return `${rest} phút`;
  if (rest === 0) return `${hours} giờ`;

  return `${hours} giờ ${rest} phút`;
}
